import { ArrowUp } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MicButton } from '../../components/speech-to-text';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { DictationLiveStatus } from './DictationLiveStatus';
import { MAX_LENGTH } from './internal-notes-utils';
import { useNoteDictation } from './useNoteDictation';
import { Group } from '@folio/components/ui/flex';

interface NoteComposerProps {
  value: string;
  onValueChange: (next: string) => void;
  onSubmit: () => void | Promise<void>;
  isSubmitting: boolean;
  speechToTextEnabled: boolean;
}

export function NoteComposer({
  value,
  onValueChange,
  onSubmit,
  isSubmitting,
  speechToTextEnabled,
}: NoteComposerProps) {
  const { t } = useTranslation();
  const { textareaRef, dictation, recording, display } = useNoteDictation({
    value,
    onValueChange,
  });

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl+Enter sends, Enter inserts a newline — brokers tend to paste
    // multi-line context into notes, so the safe default is newline-on-Enter.
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void onSubmit();
    }
  }

  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        void onSubmit();
      }}
      className="flex flex-col gap-1"
    >
      <Group
        align="end"
        gap={2}
        className="border-input bg-muted/40 focus-within:ring-ring focus-within:ring-offset-background rounded-md border px-3 py-2 focus-within:ring-2 focus-within:ring-offset-2"
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={display}
          onChange={event => onValueChange(event.target.value)}
          onKeyDown={onKeyDown}
          readOnly={recording}
          placeholder={t('internalNotes.composerPlaceholder')}
          maxLength={MAX_LENGTH}
          data-testid="internal-notes-composer"
          className={cn(
            'placeholder:text-muted-foreground flex-1 resize-none self-center bg-transparent text-sm outline-none',
            dictation.partial && 'text-muted-foreground italic',
          )}
        />
        {speechToTextEnabled && (
          <MicButton state={dictation.state} onStart={dictation.start} onStop={dictation.stop} />
        )}
        <Button
          type="submit"
          size="icon"
          className="size-7 shrink-0 rounded-full"
          disabled={!value.trim() || isSubmitting || recording}
          data-testid="internal-notes-submit"
          aria-label={t('internalNotes.send')}
        >
          <ArrowUp className="size-4" />
        </Button>
      </Group>
      <DictationLiveStatus recording={recording} dictation={dictation} />
    </form>
  );
}
